import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule, type MatCheckboxChange } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioGroup, MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule, type MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  PERMISSION_BY_ID,
  type AccessScope,
  type CreateGrantRequest,
  type GrantPermission,
  type ProjectCapabilityGrant,
} from '@app/shared/interfaces/capability-grant.interface';
import { ProjectAccessStore } from './project-access.store';
import {
  bulkKey,
  grantKey,
  scopeKey,
  type GrantCell,
  type MemberAccess,
  type RoutineAccess,
  type WorkflowAccess,
} from './project-access.mapper';

/**
 * What a grant points at, in words — a routine's name where one is known, the
 * project itself for a project-scoped capability, otherwise the permanent name
 * the grant carries.
 *
 * A routine missing from the catalog still gets a line: it means the routine was
 * removed or is not visible to the caller, and dropping it would understate what
 * the member holds.
 */
function resourceLabel(
  grant: ProjectCapabilityGrant,
  routineNames: ReadonlyMap<number, string>
): string {
  if (grant.resourceType === 'Routine') {
    return routineNames.get(grant.resourceId ?? -1) ?? `routine ${grant.resourceId}`;
  }
  if (grant.resourceType === 'Project') {
    return 'this project';
  }
  return grant.resourceName ?? '';
}

/** One line in the operator's read-only summary. */
interface MyCapability {
  /** Plain-language capability — "May run", never a permission id. */
  label: string;
  /** What it applies to. */
  resource: string;
}

/**
 * The project Access tab (EHQ-187).
 *
 * Two audiences, one component. An admin gets the configuration surface — a row
 * per member, expandable into their scope switches and grantable resources. Any
 * other member gets the same tab rendered read-only, showing what *they* have
 * been permitted to do, so "why can't I run this?" is self-answering without
 * needing the authority the answer is about.
 *
 * The vocabulary shown is deliberately concrete — "may run", "may approve" —
 * never `permission_id` or the internal capability names.
 */
@Component({
  selector: 'app-project-access',
  standalone: true,
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatTooltipModule,
  ],
  templateUrl: './project-access.component.html',
  styleUrls: ['./project-access.component.scss'],
  providers: [ProjectAccessStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectAccessComponent {
  readonly store = inject(ProjectAccessStore);

  readonly projectId = input.required<number>();
  /** The version whose workflows can be granted. Null before one resolves. */
  readonly versionId = input<number | null>(null);
  /** Whether the viewer may configure other members. Drives which view renders. */
  readonly canAdminister = input(false);

  /** The member row currently expanded, if any. One at a time — the panels are tall. */
  readonly expandedUserId = signal<number | null>(null);

  constructor() {
    // The store owns loading; the component only forwards its inputs into it.
    effect(() => {
      this.store.projectId.set(this.projectId());
      this.store.versionId.set(this.versionId());
      this.store.canAdminister.set(this.canAdminister());
    });
  }

  // ── Admin view ──────────────────────────────────────────────────────────

  toggleExpanded(userId: number): void {
    this.expandedUserId.update((current) => (current === userId ? null : userId));
  }

  /**
   * The label for a member's scope, in the words the switch uses.
   *
   * Admins get their own wording rather than "Everything": they are *exempt*
   * from scoping, not merely unrestricted, and showing them the same label as a
   * deliberately-unrestricted contributor invites an admin to wonder why the
   * switch does nothing.
   */
  scopeLabel(member: MemberAccess, scope: AccessScope): string {
    if (!member.restrictable) {
      return member.isOwner ? 'Everything (project owner)' : 'Everything (admin)';
    }
    return scope === 'All' ? 'Everything' : 'Only granted';
  }

  // ── Writes ──────────────────────────────────────────────────────────────
  //
  // Material controls flip their own state on click, and a one-way `[checked]`
  // binding whose value did not change writes nothing back. So when the server
  // refuses a change, the control keeps the state the user clicked and the panel
  // reports authority the member does not have. Every handler therefore restores
  // its control from the model when the write did not apply.

  async onViewScopeChange(
    member: MemberAccess,
    viewScope: AccessScope,
    group?: MatRadioGroup
  ): Promise<boolean> {
    const applied = await this.store.setScopes(member.userId, viewScope, member.editScope);
    if (!applied && group) {
      group.value = member.viewScope;
    }
    return applied;
  }

  async onEditScopeChange(
    member: MemberAccess,
    editScope: AccessScope,
    group?: MatRadioGroup
  ): Promise<boolean> {
    const applied = await this.store.setScopes(member.userId, member.viewScope, editScope);
    if (!applied && group) {
      group.value = member.editScope;
    }
    return applied;
  }

  async onPublishChange(member: MemberAccess, event?: MatSlideToggleChange): Promise<boolean> {
    const applied = await this.store.toggleGrant(
      member.userId,
      'PublishVersion',
      { resourceType: 'Project' },
      member.publish
    );
    if (!applied && event) {
      event.source.checked = member.publish.granted;
    }
    return applied;
  }

  async onRoutineToggle(
    member: MemberAccess,
    routine: RoutineAccess,
    permission: 'RunRoutine' | 'ApproveReview',
    event?: MatCheckboxChange
  ): Promise<boolean> {
    const cell = permission === 'RunRoutine' ? routine.run : routine.approve;
    const applied = await this.store.toggleGrant(
      member.userId,
      permission,
      { resourceType: 'Routine', resourceId: routine.routineId },
      cell
    );
    if (!applied && event) {
      event.source.checked = cell.granted;
    }
    return applied;
  }

  async onWorkflowToggle(
    member: MemberAccess,
    workflow: WorkflowAccess,
    permission: 'ViewResource' | 'EditResource',
    event?: MatCheckboxChange
  ): Promise<boolean> {
    const cell = permission === 'ViewResource' ? workflow.view : workflow.edit;
    const applied = await this.store.toggleGrant(
      member.userId,
      permission,
      { resourceType: 'Workflow', resourceName: workflow.name },
      cell
    );
    if (!applied && event) {
      event.source.checked = cell.granted;
    }
    return applied;
  }

  /** Grants `permission` on every routine at once. */
  checkAllRoutines(
    member: MemberAccess,
    permission: 'RunRoutine' | 'ApproveReview'
  ): Promise<boolean> {
    const grants: CreateGrantRequest[] = member.routines.map((routine) => ({
      userId: member.userId,
      permission,
      resourceType: 'Routine',
      resourceId: routine.routineId,
    }));
    return this.store.grantAll(member.userId, permission, grants);
  }

  /** Revokes `permission` on every routine that currently has it. */
  uncheckAllRoutines(
    member: MemberAccess,
    permission: 'RunRoutine' | 'ApproveReview'
  ): Promise<boolean> {
    const ids = member.routines
      .map((routine) => (permission === 'RunRoutine' ? routine.run : routine.approve))
      .filter((cell) => cell.granted && cell.grantId !== undefined)
      .map((cell) => cell.grantId as number);
    return this.store.revokeAll(member.userId, permission, ids);
  }

  /** Grants `permission` on every workflow at once. */
  checkAllWorkflows(
    member: MemberAccess,
    permission: 'ViewResource' | 'EditResource'
  ): Promise<boolean> {
    // Only workflows in the displayed version. The orphan rows exist as a
    // *revoke* affordance for authority that already exists elsewhere; granting
    // through them would confer authority on a target the admin cannot see, and
    // did not ask for. uncheck-all keeps them, deliberately — the asymmetry is
    // the point.
    const grants: CreateGrantRequest[] = member.workflows
      .filter((workflow) => workflow.inVersion)
      .map((workflow) => ({
        userId: member.userId,
        permission,
        resourceType: 'Workflow',
        resourceName: workflow.name,
      }));
    return this.store.grantAll(member.userId, permission, grants);
  }

  /**
   * Revokes `permission` on every workflow that currently has it.
   *
   * Implied view cells are skipped — they have no row to revoke, and their
   * authority goes away when the edit grant does.
   */
  uncheckAllWorkflows(
    member: MemberAccess,
    permission: 'ViewResource' | 'EditResource'
  ): Promise<boolean> {
    const ids = member.workflows
      .map((workflow) => (permission === 'ViewResource' ? workflow.view : workflow.edit))
      .filter((cell) => cell.granted && !cell.implied && cell.grantId !== undefined)
      .map((cell) => cell.grantId as number);
    return this.store.revokeAll(member.userId, permission, ids);
  }

  // ── Per-control keys ────────────────────────────────────────────────────
  //
  // Each control asks whether *it* is saving, rather than sharing one flag. A
  // shared flag is bound to every `[disabled]` on the panel, so one checkbox
  // click re-evaluates all of them and the whole panel reacts to a change in one
  // cell.

  routineKey(
    member: MemberAccess,
    routine: RoutineAccess,
    permission: 'RunRoutine' | 'ApproveReview'
  ): string {
    return grantKey(member.userId, permission, {
      resourceType: 'Routine',
      resourceId: routine.routineId,
    });
  }

  workflowKey(
    member: MemberAccess,
    workflow: WorkflowAccess,
    permission: 'ViewResource' | 'EditResource'
  ): string {
    return grantKey(member.userId, permission, {
      resourceType: 'Workflow',
      resourceName: workflow.name,
    });
  }

  publishKey(member: MemberAccess): string {
    return grantKey(member.userId, 'PublishVersion', { resourceType: 'Project' });
  }

  scopesKey(member: MemberAccess): string {
    return scopeKey(member.userId);
  }

  columnKey(member: MemberAccess, permission: GrantPermission): string {
    return bulkKey(member.userId, permission);
  }

  /** Why a cell is checked but not clickable, for its tooltip. */
  cellTooltip(cell: GrantCell): string {
    return cell.implied ? 'Visible because this member may edit it' : '';
  }

  // ── Operator read-only view ─────────────────────────────────────────────

  /**
   * The viewer's own capabilities, in plain language.
   *
   * Built from the grant rows joined against the routine catalog so a routine is
   * named rather than numbered. A grant whose routine is not in the catalog is
   * still listed — it means the routine was removed or is not visible, and
   * silently dropping the line would misreport what the member holds.
   */
  readonly myCapabilities = computed<MyCapability[]>(() => {
    const routineNames = new Map(this.store.routines().map((r) => [r.routineId, r.name]));
    const labels: Record<GrantPermission, string> = {
      RunRoutine: 'May run',
      ApproveReview: 'May approve reviews for',
      ViewResource: 'May see',
      EditResource: 'May edit',
      PublishVersion: 'May publish versions of',
    };

    return this.store.myGrants().flatMap((grant) => {
      const permission = PERMISSION_BY_ID.get(grant.permissionId);
      if (!permission) {
        return [];
      }
      return [{ label: labels[permission], resource: resourceLabel(grant, routineNames) }];
    });
  });

  /**
   * Whether the viewer's own visibility is restricted.
   *
   * Read from the server's answer rather than inferred from the grant list: an
   * admin is exempt even with grants on file, and a restricted member with no
   * grants sees nothing where an empty list would read as unrestricted.
   */
  readonly myVisibilityRestricted = computed(() => this.store.visibility()?.restricted ?? false);

  readonly myVisibleWorkflows = computed(() => this.store.visibility()?.visibleWorkflows ?? []);
}
